import { BoardStateDto, CellDto, CellType, Position } from './types.js';

export class GameBoard {
  private boardElement: HTMLElement;
  private cells: HTMLElement[][] = [];
  private onCellClick?: (position: Position) => void;
  private boardState?: BoardStateDto;
  private validMoves: Set<string> = new Set();

  constructor(boardElement: HTMLElement) {
    this.boardElement = boardElement;
    this.initializeBoard();
  }

  private initializeBoard() {
    this.boardElement.innerHTML = '';
    this.cells = [];

    for (let row = 0; row < 8; row++) {
      this.cells[row] = [];
      for (let col = 0; col < 8; col++) {
        const cell = this.createCell(row, col);
        this.cells[row][col] = cell;
        this.boardElement.appendChild(cell);
      }
    }
  }

  private createCell(row: number, col: number): HTMLElement {
    const cell = document.createElement('button');
    cell.className = 'board-cell';
    cell.dataset.row = row.toString();
    cell.dataset.col = col.toString();
    
    cell.addEventListener('click', () => {
      if (this.onCellClick && !cell.classList.contains('board-cell--disabled')) {
        this.onCellClick({ row, col });
      }
    });

    return cell;
  }

  private positionToKey(position: Position): string {
    return `${position.row},${position.col}`;
  }

  updateBoard(boardState: BoardStateDto) {
    this.boardState = boardState;
    
    // Update valid moves
    this.validMoves.clear();
    boardState.validMoves.forEach(move => {
      this.validMoves.add(`${move[0]},${move[1]}`);
    });

    // Update cells
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const cell = this.cells[row][col];
        const cellData = boardState.cells[row][col];
        this.updateCell(cell, cellData, { row, col });
      }
    }
  }

  private updateCell(cellElement: HTMLElement, cellData: CellDto, position: Position) {
    // Clear previous content and classes
    cellElement.innerHTML = '';
    cellElement.className = 'board-cell';
    
    const posKey = this.positionToKey(position);
    
    // Add disabled class if not a valid move
    if (!this.validMoves.has(posKey)) {
      cellElement.classList.add('board-cell--disabled');
    }

    // Render piece if present
    if (cellData.type !== CellType.Empty) {
      const piece = this.createPiece(cellData);
      cellElement.appendChild(piece);
    }

    // Add special cell styling
    if (cellData.type === CellType.Nexus) {
      cellElement.classList.add('board-cell--nexus');
    }
  }

  private createPiece(cellData: CellDto): HTMLElement {
    const piece = document.createElement('div');
    piece.className = 'game-piece';

    switch (cellData.type) {
      case CellType.Migo:
        if (cellData.playerIndex === 0) {
          piece.classList.add('game-piece--white');
        } else {
          piece.classList.add('game-piece--black');
        }
        break;

      case CellType.Yugo:
        piece.classList.add('game-piece--yugo');
        if (cellData.playerIndex === 0) {
          piece.classList.add('game-piece--white');
        } else {
          piece.classList.add('game-piece--black');
        }
        
        // Add Yugo type indicator based on yugoCount
        if (cellData.yugoCount) {
          const indicator = document.createElement('div');
          indicator.className = 'yugo-indicator';
          
          // Apply specific class based on yugo count (1-4)
          switch(cellData.yugoCount) {
            case 1:
              indicator.classList.add('yugo-indicator--single');
              break;
            case 2:
              indicator.classList.add('yugo-indicator--double');
              break;
            case 3:
              indicator.classList.add('yugo-indicator--triple');
              break;
            case 4:
              indicator.classList.add('yugo-indicator--quadruple');
              break;
            default:
              // Fallback for unexpected values
              indicator.classList.add('yugo-indicator--single');
              indicator.textContent = cellData.yugoCount.toString();
          }
          
          piece.appendChild(indicator);
        }
        break;

      case CellType.Nexus:
        piece.classList.add('game-piece--nexus');
        if (cellData.playerIndex === 0) {
          piece.classList.add('game-piece--white');
        } else {
          piece.classList.add('game-piece--black');
        }
        break;
    }

    return piece;
  }

  highlightLastMove(position: Position) {
    // Remove previous last move highlight
    this.boardElement.querySelectorAll('.board-cell--last-move').forEach(cell => {
      cell.classList.remove('board-cell--last-move');
    });

    // Add highlight to new last move
    const cell = this.cells[position.row][position.col];
    if (cell) {
      cell.classList.add('board-cell--last-move');
    }
  }

  setCellClickHandler(handler: (position: Position) => void) {
    this.onCellClick = handler;
  }

  setDisabled(disabled: boolean) {
    this.boardElement.classList.toggle('board--disabled', disabled);
    
    this.cells.forEach(row => {
      row.forEach(cell => {
        (cell as HTMLButtonElement).disabled = disabled;
        if (disabled) {
          cell.classList.add('board-cell--disabled');
        }
      });
    });
  }

  animateMove(position: Position) {
    const cell = this.cells[position.row][position.col];
    if (cell) {
      const piece = cell.querySelector('.game-piece');
      if (piece) {
        // Trigger piece placement animation
        piece.classList.add('game-piece--animate');
        setTimeout(() => {
          piece.classList.remove('game-piece--animate');
        }, 300);
      }
    }
  }

  getPositionFromAlgebraic(notation: string): Position | null {
    if (notation.length !== 2) return null;
    
    const col = notation.charAt(0).toLowerCase().charCodeAt(0) - 'a'.charCodeAt(0);
    const row = parseInt(notation.charAt(1)) - 1;
    
    if (col < 0 || col > 7 || row < 0 || row > 7) return null;
    
    return { row, col };
  }

  getAlgebraicFromPosition(position: Position): string {
    if (position.row < 0 || position.row > 7 || position.col < 0 || position.col > 7) {
      return '??';
    }
    
    const col = String.fromCharCode('a'.charCodeAt(0) + position.col);
    const row = (position.row + 1).toString();
    
    return col + row;
  }

  // Utility methods for testing and debugging
  getBoardState(): BoardStateDto | undefined {
    return this.boardState;
  }

  getValidMoves(): Position[] {
    return Array.from(this.validMoves).map(key => {
      const [row, col] = key.split(',').map(Number);
      return { row, col };
    });
  }

  reset() {
    this.initializeBoard();
    this.boardState = undefined;
    this.validMoves.clear();
  }

  showInvalidMoveAnimation(position: Position) {
    const cell = this.cells[position.row][position.col];
    if (cell) {
      // Add a shake animation class
      cell.classList.add('invalid-move');
      // Keep the class longer so animation can complete multiple times
      setTimeout(() => {
        cell.classList.remove('invalid-move');
      }, 1000);
    }
  }
}